import { clsx } from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Option } from "web/react/components/select/types";
import SizeGuide from "web/react/components/size-guide/size-guide";
import { ErrorText } from "web/react/components/size-picker/error-text";
import { SizePicker } from "web/react/components/size-picker/size-picker";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { ProductScreenSize } from "web/types/serializers";
import * as styles from "./size-area.css";

interface SizeAreaProps {
    sizes: ProductScreenSize[];
    isSizePickerExpanded: boolean;
    handleSizeLinkClick: (expanded: boolean, shouldSendAnalytic?: boolean) => void;
    isComparison?: boolean;
    isModal?: boolean;
}

export function SizeArea({
    sizes,
    isSizePickerExpanded,
    handleSizeLinkClick,
    isComparison,
    isModal,
}: SizeAreaProps): React.ReactElement | null {
    const sizeAreaRef = useRef<HTMLDivElement>(null);

    const {
        sizePicker: {
            showSizePicker,
            schemaLabels,
            userDefaultSchema,
            sizeGuideLink,
            setSelectedSizeOption,
            setError,
            setComparisonError,
            error,
            comparisonError,
        },
        activeProduct: { selectedSizeOption },
        productPurchaseType,
    } = useInStockProductPageContext();

    // have a prop that tracks if the user has already been scrolled to the size picker error.
    const [scrolled, setScrolled] = useState(false);
    const isExpressCheckout = useMemo(
        () => productPurchaseType === ProductPurchaseType.EXPRESS_CHECKOUT,
        [productPurchaseType]
    );
    const isCheckout = useMemo(
        () => productPurchaseType === ProductPurchaseType.CHECKOUT,
        [productPurchaseType]
    );

    const [isAnchorVisible] = useIntersectionObserver(
        {
            rootMargin: "0px",
            threshold: 1,
        },
        sizeAreaRef
    );

    // scroll to the size picker error, only if we cannot see the size picker,
    // and only if we're showing the error for the first time.
    // We can't use `useIntersectionObserver({once:true})` for this aim, because
    // the user might scroll past the size picker before clicking 'buy now', i.e.
    // intersection observer will just think the size picker is visible (when it
    // isn't)
    useEffect(() => {
        if (((isComparison && comparisonError) || error) && sizeAreaRef.current) {
            if (!isAnchorVisible && !scrolled) {
                sizeAreaRef.current.scrollIntoView({ behavior: "smooth" });
            }
            setScrolled(true);
        }
    }, [isComparison, comparisonError, error, isAnchorVisible]);

    const sendAnalyticsOnSelect = useCallback(
        (option: Option, isComparison: boolean) => {
            if (productPurchaseType === ProductPurchaseType.CHECKOUT) {
                analytics.event({
                    category: "size",
                    action: "clicked",
                    customData: { size: option.selectedText },
                });
            } else {
                const action = option.disabled ? "select_oos_alert" : "select_in_stock_size";
                analytics.event({
                    category: isComparison ? "compare_prices_s" : "size_picker", // compare_prices_size_picker truncated to 16 chars
                    action,
                    label: option.selectedText.substring(0, 64),
                    subType: `${productPurchaseType}.size_picker.${action}`,
                    lock: true,
                });
            }
        },
        [productPurchaseType]
    );

    const handleSelect = useCallback(
        (option: Option, isComparison: boolean) => {
            setError(false);
            setComparisonError(false);
            if (isExpressCheckout) {
                analytics.event({
                    category: "express_checkout",
                    action: "changed_size",
                });
                setSelectedSizeOption(option.value);
                return;
            }
            sendAnalyticsOnSelect(option, isComparison);
            setSelectedSizeOption(option.value);
        },
        [
            isExpressCheckout,
            sendAnalyticsOnSelect,
            setComparisonError,
            setError,
            setSelectedSizeOption,
        ]
    );

    const showSizeGuide = useMemo(
        () => !isComparison && !isExpressCheckout && !isModal && !isCheckout,
        [isComparison, isExpressCheckout, isModal]
    );
    const shouldShow = useMemo(
        () => showSizePicker || (!showSizePicker && !!isModal),
        [isModal, showSizePicker]
    );

    if (!shouldShow) {
        return null;
    }

    return (
        <div
            ref={sizeAreaRef}
            className={clsx(styles.sizePicker, {
                ["hidden-tablet"]: isComparison,
                [styles.sizePickerComparison]: isComparison,
            })}
            data-testid="size-area"
        >
            {showSizeGuide && <SizeGuide sizeGuideLink={sizeGuideLink} />}
            <SizePicker
                error={isComparison ? comparisonError : error}
                onSelect={handleSelect}
                isExpanded={isSizePickerExpanded}
                schemas={schemaLabels}
                defaultSelectText={gettext("product.buy_area.select_size.label")} // "Select size to see lowest price"
                userDefaultSchema={userDefaultSchema}
                setExpanded={handleSizeLinkClick}
                sizes={sizes}
                productType={productPurchaseType}
                selectedOption={selectedSizeOption || undefined}
                isExpressCheckout={isExpressCheckout}
                isComparison={!!isComparison}
                showDisabledOptions
            />
            {isComparison ? comparisonError && <ErrorText /> : error && <ErrorText />}
        </div>
    );
}
